/**
 * Display contract for Action Plan numbers (P0-4).
 *
 * One rule, applied everywhere: a number that was never measured is rendered as
 * unavailable, not as zero. Those are opposite claims — "we hold no cost data"
 * versus "this costs nothing" — and the codebase had been collapsing the first
 * into the second at every render site, because `formatCurrency(undefined)`
 * returned '$0'.
 *
 * The guards here are load-bearing rather than defensive. Server
 * `strictNullChecks` is off, so no server-side type says these fields are
 * present; and Action Plans are stored as `raw_json` documents, so plans written
 * before a field existed keep arriving without it indefinitely. A component that
 * assumes presence will throw on real customer data.
 *
 * S4b stops the *server* from fabricating and persisting such values in the
 * first place, and moves ROI and payback server-side where provenance can be
 * enforced at the point the number is born. This module is the render boundary,
 * not the fix for that.
 */

/**
 * The single token for "we do not have this number". One string, so a test can
 * assert its absence from a subtree and a reader can trust the sweep.
 */
export const UNAVAILABLE_LABEL = 'Unavailable';

/**
 * Narrow anything to a usable number. `NaN` and `±Infinity` are rejected
 * alongside null and undefined: they are arithmetic accidents, and rendering
 * them is strictly worse than admitting the gap.
 */
export function finiteOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Currency, with the sign outside the symbol and magnitude suffixes. */
export function formatCurrency(value: number | null | undefined): string {
  const n = finiteOrNull(value);
  if (n === null) return UNAVAILABLE_LABEL;

  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
}

/** A percentage. `signed` prints its own sign so no template hardcodes one. */
export function formatPercent(
  value: number | null | undefined,
  options: { digits?: number; signed?: boolean } = {},
): string {
  const n = finiteOrNull(value);
  if (n === null) return UNAVAILABLE_LABEL;
  const { digits = 0, signed = false } = options;
  const sign = signed && n > 0 ? '+' : '';
  return `${sign}${n.toFixed(digits)}%`;
}

/** A score to one decimal, e.g. a FAIR pillar. */
export function formatScore(value: number | null | undefined): string {
  const n = finiteOrNull(value);
  return n === null ? UNAVAILABLE_LABEL : n.toFixed(1);
}

/** A duration in whole weeks. */
export function formatWeeks(value: number | null | undefined): string {
  const n = finiteOrNull(value);
  return n === null ? UNAVAILABLE_LABEL : `${n.toFixed(0)} weeks`;
}

/** A duration in months, one decimal. */
export function formatMonths(value: number | null | undefined): string {
  const n = finiteOrNull(value);
  return n === null ? UNAVAILABLE_LABEL : `${n.toFixed(1)} mo`;
}

/**
 * A return, expressed the way it is usually quoted.
 *
 * At or above 100% the multiple is more readable, but it must not be *rounded*
 * to a whole multiple: the previous `Math.round(roi / 100)` turned 150% into
 * "2x", overstating the return by a third at the single number most likely to be
 * repeated in a boardroom.
 */
export function formatReturn(roiPct: number | null | undefined): string {
  const n = finiteOrNull(roiPct);
  if (n === null) return UNAVAILABLE_LABEL;
  if (Math.abs(n) >= 100) return `${(n / 100).toFixed(1)}x`;
  return `${n.toFixed(0)}%`;
}

/**
 * `numerator / denominator * 100`, or null when the ratio is undefined.
 *
 * A zero denominator is not a zero ratio; it is an absent one. Rather than
 * testing for `d === 0` specifically, the *result* is checked: that also covers
 * overflow from a denominator merely close to zero, and it is one guard instead
 * of two overlapping ones. An explicit `d === 0` clause here was strictly
 * redundant — no test could distinguish it, which is the definition of a guard
 * that cannot fail.
 *
 * This is what keeps `Infinity` and `NaN` out of both the card text and the
 * inline `width:` styles that used to receive them.
 */
export function safeRatioPct(
  numerator: number | null | undefined,
  denominator: number | null | undefined,
): number | null {
  const n = finiteOrNull(numerator);
  const d = finiteOrNull(denominator);
  if (n === null || d === null) return null;
  return finiteOrNull((n / d) * 100);
}

/** A CSS width percentage, clamped, or '0%' when the ratio is undefined. */
export function barWidthPct(
  numerator: number | null | undefined,
  denominator: number | null | undefined,
): string {
  const pct = safeRatioPct(numerator, denominator);
  if (pct === null) return '0%';
  return `${Math.min(100, Math.max(0, pct)).toFixed(2)}%`;
}

/**
 * The mean of the four FAIR pillars, or null unless all four are present.
 *
 * Averaging over the pillars that happen to be present would silently change
 * the denominator and report a higher score for a less complete assessment.
 */
export function fairAverageOrNull(
  scores: { F?: number | null; A?: number | null; I?: number | null; R?: number | null } | null | undefined,
): number | null {
  if (!scores) return null;
  const pillars = [scores.F, scores.A, scores.I, scores.R].map(finiteOrNull);
  if (pillars.some((p) => p === null)) return null;
  return (pillars as number[]).reduce((a, b) => a + b, 0) / 4;
}

/**
 * Total the values that exist, and say how many did not.
 *
 * `reduce((sum, t) => sum + (t.value_usd || 0), 0)` reports $0 for a set of
 * tasks that are merely unpriced, which is the absence-as-zero conflation again
 * with an aggregate hiding it. `total` is null when nothing was priced;
 * `missing` is disclosed next to the figure so a partial total is never mistaken
 * for a complete one.
 */
export function sumTaskValues(
  tasks: ReadonlyArray<{ value_usd?: { value: number | null; provenance: string } | null }>,
): { total: number | null; missing: number; counted: number } {
  let total = 0;
  let counted = 0;
  let missing = 0;

  for (const task of tasks) {
    const q = task.value_usd;
    const v = q && q.provenance !== 'unavailable' ? finiteOrNull(q.value) : null;
    if (v === null) missing += 1;
    else {
      total += v;
      counted += 1;
    }
  }

  // AGGREGATION RULE (S4d): one unpriced task and there is no total. A partial
  // sum labelled "Value" is a subtotal presented as a total, and every consumer
  // downstream — ROI, payback, the Matrix axis — treats it as complete.
  return { total: missing === 0 && counted > 0 ? total : null, missing, counted };
}

/** Label for a task whose effort estimate is not available. */
export const EFFORT_UNAVAILABLE_LABEL = 'Effort unavailable';

/**
 * Compact effort for a task card or row: `3w`, `0w`, or "Effort unavailable".
 *
 * `effort_weeks` is `number | null` (2A): `action_plan_tasks.estimated_hours` is nullable,
 * and the read path used to report a missing estimate as 0. So this must NOT collapse null
 * into `0w` — that is the fabrication being removed, restated in the formatter.
 *
 * A genuine `0` renders as `0w`, because zero weeks is a measurement and not an absence.
 */
export function formatEffortWeeks(value: number | null | undefined): string {
  const n = finiteOrNull(value);
  if (n === null) return EFFORT_UNAVAILABLE_LABEL;
  // Sub-week efforts are real: 20 stored hours is 0.5 weeks, and rounding that to `0w`
  // would read as no effort at all.
  return Number.isInteger(n) ? `${n}w` : `${n.toFixed(1)}w`;
}

/** Label for a task that cannot be placed on a schedule because effort is unavailable. */
export const SCHEDULE_UNAVAILABLE_LABEL = 'Schedule unavailable';

/**
 * How a task may appear on a schedule (2A follow-up).
 *
 * The Timeline previously drew a bar for EVERY task from `start_week`/`end_week`, so a task
 * with no effort estimate still got a bar — and because the generator defines
 * `end_week = start_week + effort_weeks - 1`, an unestimated task's stored range collapses to
 * a zero- or negative-width sliver at whatever week the document happened to record. Changing
 * only the tooltip left that intact.
 *
 * Classification is by `effort_weeks`, which is the availability axis 2A established. The week
 * numbers are geometry; effort decides whether there is a schedule at all.
 */
export type ScheduleKind = 'scheduled' | 'milestone' | 'unavailable';

export function scheduleKindOf(effortWeeks: number | null | undefined): ScheduleKind {
  const n = finiteOrNull(effortWeeks);
  if (n === null) return 'unavailable';
  // A genuine zero is a real, dateable event with no duration — a milestone, not a bar.
  // `end_week` for it is `start_week - 1`, which any width formula renders as invisible or
  // negative, so it needs its own marker rather than a degenerate bar.
  return n === 0 ? 'milestone' : 'scheduled';
}

/**
 * Inclusive week span, in columns.
 *
 * The generator defines `end_week = start_week + effort_weeks - 1`, so both endpoints are
 * OCCUPIED. `(end_week - start_week)` therefore undercounts by exactly one: a one-week task
 * came out zero-width and every longer task was drawn a week short.
 */
export function inclusiveWeekSpan(startWeek: number, endWeek: number): number {
  return Math.max(0, endWeek - startWeek + 1);
}

/**
 * Label for a phase (or plan) whose progress cannot be computed.
 *
 * Used when there are no resolvable tasks to compute over. Not "0%", which asserts that none of
 * the work is done rather than that there is no work to measure.
 */
export const PROGRESS_UNAVAILABLE_LABEL = 'No tasks';

/**
 * The id of the shared task drawer rendered by `ActionPlanPanel` (2B part 2).
 *
 * Task cards reference it via `aria-controls`; the drawer element carries it. Declared
 * here — the display-contract module — rather than in a component, so a view and the
 * drawer can both import it without a component importing a component.
 */
export const AP_TASK_DRAWER_ID = 'ap-task-drawer';

/**
 * The label for a status the wire delivered but the vocabulary does not contain.
 *
 * Not "Not started": a malformed status is missing information, and `not_started` is a
 * real stored state someone can observe. One exported constant so tests can assert the
 * fail-closed path by name.
 */
export const STATUS_UNAVAILABLE_LABEL = 'Status unavailable';

/**
 * Human label for a PERSISTED status, including the non-settable ones (2B part 2).
 *
 * `blocked` and `skipped` are real storable states (migration 021:137, retained by 141)
 * and must render as themselves — collapsing them into "Not started" fabricates a state.
 * One implementation, shared by the Matrix cards and the task drawer, so two surfaces
 * cannot describe the same status with different words.
 *
 * FAIL CLOSED on anything else (36cd088 follow-up): the parameter type says the five
 * states, but this runs against wire data, and a malformed value must not be silently
 * presented as a state somebody recorded. The default arm is the runtime backstop the
 * type system cannot provide.
 */
export function getStatusLabel(
  status: 'not_started' | 'in_progress' | 'completed' | 'blocked' | 'skipped',
): string {
  switch (status) {
    case 'completed': return 'Completed';
    case 'in_progress': return 'In progress';
    case 'not_started': return 'Not started';
    case 'blocked': return 'Blocked';
    case 'skipped': return 'Skipped';
    default: return STATUS_UNAVAILABLE_LABEL;
  }
}
